import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { WalletProvider } from "./WalletProvider";
import { BrandToastProvider } from "../components/brand/BrandToast";
import i18n from "../i18n/config";
import type { ReactNode } from "react";

/**
 * QueryClient configuration with optimized defaults
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 60000, // 60 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
    },
    mutations: {
      retry: false, // Solana transactions use useSolanaTransaction's blockhash-aware retry
    },
  },
});

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Root providers component that composes all application providers
 * Order matters:
 * - I18nextProvider must be outermost to ensure all components have access to translations
 * - WalletProvider must wrap QueryClientProvider for wallet hooks to work
 */
export function AppProviders({ children }: AppProvidersProps) {
  // Type assertion needed due to i18next v25+ type branding mismatch in monorepo
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const i18nInstance = i18n as any;

  return (
    <I18nextProvider i18n={i18nInstance}>
      <WalletProvider>
        <QueryClientProvider client={queryClient}>
          {children}
          <BrandToastProvider />
        </QueryClientProvider>
      </WalletProvider>
    </I18nextProvider>
  );
}
